/**
 * 이상한 방 - 파티 리빌의 재료 (순서 옵션 · 스텝의 얼굴 · 마지막 자리 판정)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 5절(웃김 설계 포인트 3번 "야 저거 맞잖아!!" · 6번 "저걸 못 봤다고???")
 *            `이상한방-상태모델-v0.1.md` 11.2절(클라이언트가 가진 것 - 두 방과 뷰) · 11.3절(리빌 연출은 클라이언트의 것)
 *            로드맵 M5(`matchSessionReveal` - 클라이언트 `changes`(리빌부터 있다) + `revealOrder(…, 'easiest')` + `foundBy`)
 *
 * ## 웃음의 주어가 한 사람에서 전원으로 옮겨 간다
 *
 * 싱글 리빌의 마지막 자리는 **가장 교묘한 것**이다 - "저걸 못 봤다고???"는 내 실수를 향한 말이다. 여럿이서는 반대다.
 * 여러 사람이 **다 같이** 놓친 것 중 **가장 쉬웠던 것**이 마지막에 오면 "저걸 아무도 못 봤다고???"가 되고, 웃음의 주어가
 * 전원의 실수가 된다(`revealOrder.ts` 머리말). 그 순서는 `orderChanges(…, 'easiest')`가 이미 갖고 있고, 이 파일은
 * 그 값을 `RevealSequenceOptions.finale`로 넘길 뿐이다 - 순서의 뜻을 여기 다시 적지 않는다. **싱글의 순서는 한 글자도
 * 안 바뀐다**(`reveal.ts`는 옵션이 없으면 `'hardest'`다).
 *
 * ## 스텝의 얼굴은 셋 중 하나다
 *
 *  - 남이 찾았다 → 그 사람의 이름표("OO이 찾았다"). 이름은 뷰(`ClientView.players`)에서 찾고 사전을 거치지 않는다.
 *  - 내가 찾았다 → "내가 찾았다". 내 이름표를 내 화면에 찍으면 남의 화면과 같은 얼굴이라 "나"라는 사실이 안 읽힌다.
 *  - 아무도 못 찾았다 → "아무도 못 봤다". 그중 **마지막 미발견**(= 미발견 중 가장 쉬운 것)만이 `finale`이고, 그 한 번에만
 *    대형 스탬프(`juice.nobodySaw`)가 터진다. 미발견마다 큰 스탬프를 찍으면 다섯을 놓친 판에서 같은 스탬프를 다섯 번 보게
 *    되고, 세 번째부터는 웃음이 아니라 리듬이다("마지막이 가장 크게" - `reveal.ts` 머리말 1번, 봉투의 열 장 연출도 같은 원칙).
 *
 * ## 이 파일에 규칙이 없다
 *
 * 누가 찾았는지는 호스트가 굳힌 `found[].by`이고, 무엇이 미발견인지는 스텝의 `found`(호스트의 `found`에서 나온다)다.
 * 여기는 그 둘을 화면 모듈(`ui/revealParty.ts`)이 아는 모양으로 옮기고, "마지막 미발견인가"라는 **순서 위의 질문**에
 * 답할 뿐이다. `matchSessionView.ts`처럼 산술이 없어야 하는 자리는 아니지만(인덱스 하나를 본다) 규칙은 없다.
 *
 * 세션 접착층의 한 조각이라 `ui/`의 **타입만** 가져온다(`matchSessionView.ts`와 같은 예외 안에 있다).
 */

import type { PlayerId } from '../core/types'
import type { RevealPartyFace, RevealPartyOwner } from '../ui/revealParty'
import type { MatchFound } from './match/matchTypes'
import type { ClientView } from './match/matchView'
import { nameOf } from './matchSessionView'
import type { RevealSequenceOptions, RevealStep } from './reveal'

/* --- 순서 ------------------------------------------------------------------------- */

/**
 * 파티 리빌의 시퀀스 옵션. 마지막 자리는 가장 쉬웠는데 다 놓친 것(`'easiest'`), 이름표는 발견 목록의 `by`다.
 * `found`는 뷰의 것(호스트가 굳힌 공유 목록) 그대로 넘긴다 - 여기서 다시 세거나 걸러 내지 않는다.
 */
export function partyRevealSequence(found: readonly MatchFound[]): RevealSequenceOptions {
  return {
    finale: 'easiest',
    foundBy: new Map<string, PlayerId>(found.map((entry) => [entry.changeId, entry.by])),
  }
}

/* --- 마지막 자리 -------------------------------------------------------------------- */

/**
 * 이 스텝이 **마지막 미발견**인가 - 미발견이고, 다음 스텝이 없거나 발견분이다. 미발견은 전부 앞에 오므로
 * (`revealOrder.orderChanges`) 그 경계가 곧 "가장 쉬웠는데 다 놓친 것"의 자리다. 발견분 스텝은 언제나 거짓이다.
 */
export function isPartyFinale(steps: readonly RevealStep[], index: number): boolean {
  const step = steps[index]
  if (step === undefined || step.found) return false
  const next = steps[index + 1]
  return next === undefined || next.found
}

/* --- 주인 -------------------------------------------------------------------------- */

/**
 * 이 스텝의 이상한 곳을 **누가 만들었나**(M11). 주인은 호스트가 굳힌 `MatchChange.owner`이고, 여기서 좌표로 구획을
 * 되짚지 않는다 - 좌표 유도는 어디에도 없다(그 권위가 둘이 되지 않게 `matchZones`가 일부러 비워 둔 자리다). 리빌 스텝의
 * `change`는 `Change`로 좁혀져 있으므로(싱글과 공유하는 타입) 뷰의 정답표에서 같은 id를 찾아 주인을 읽는다.
 *
 * 정답표가 아직 없거나(리빌 전) 좌석이 사라졌으면 `null`이다 - 이름 없는 줄을 세우는 대신 줄을 접는다.
 */
function ownerOf(step: RevealStep, view: ClientView): RevealPartyOwner {
  const owner = view.changes?.find((change) => change.id === step.change.id)?.owner
  if (owner === undefined) return null
  return { name: nameOf(view, owner), isMe: owner === view.me }
}

/* --- 얼굴 ------------------------------------------------------------------------- */

/**
 * 스텝 하나의 얼굴(머리말의 셋). 발견분인데 `foundBy`가 없으면(싱글 시퀀스를 파티 화면에 넘긴 배선 사고) 이름표 없이
 * "아무도 못 봤다"를 찍는 대신 **누구 것도 아닌 발견**으로 두지 않는다 - 그 경우는 `null`을 돌려 화면이 아무것도 안 찍는다.
 * 거짓 이름표("아무도 못 봤다"인데 누군가 찾았다)는 빈 자리보다 나쁘다.
 *
 * 주인(M11)은 셋 모두에 붙는다 - 아무도 못 찾은 것은 그 사람의 점수이고(규칙 5), 찾힌 것은 그 사람이 못 숨긴 것이다.
 */
export function partyRevealFace(
  step: RevealStep,
  index: number,
  steps: readonly RevealStep[],
  view: ClientView,
): RevealPartyFace | null {
  const owner = ownerOf(step, view)
  if (!step.found) return { kind: 'nobody', finale: isPartyFinale(steps, index), owner }
  if (step.foundBy === undefined) return null
  return step.foundBy === view.me
    ? { kind: 'mine', owner }
    : { kind: 'found', name: nameOf(view, step.foundBy), owner }
}
